import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import FocusTrap from 'focus-trap-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import Logo from '@/components/layout/Logo';
import { buttonVariants, Button } from '@/components/ui/button';

const nav = [
  { to: '/',           label: 'Home',       n: '00' },
  { to: '/work',       label: 'Work',       n: '01' },
  { to: '/industries', label: 'Industries', n: '02' },
  { to: '/services',   label: 'Services',   n: '03' },
  { to: '/process',    label: 'Process',    n: '04' },
  { to: '/about',      label: 'About',      n: '05' },
];

export const Navigation = () => {
  const [open, setOpen]     = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => { 
      document.body.style.overflow = ''; 
      document.documentElement.style.overflow = '';
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-background/85 backdrop-blur-xl border-b border-border' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto container-editorial h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" aria-label="TechsFlow home">
            <Logo className="w-7 h-7" />
            <span className="font-serif text-xl md:text-2xl leading-none tracking-tight">
              TechsFlow
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {nav.map((n) => (
                <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium transition-colors ${
                    isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span>{n.label}</span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-dot"
                        className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-sienna"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-full bg-foreground text-background hover:bg-sienna transition-colors"
            >
              Start a project
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-background lg:hidden"
          >
            <FocusTrap focusTrapOptions={{ initialFocus: false, clickOutsideDeactivates: true }}>
              <div className="flex flex-col h-full">
                <div className="h-16 md:h-20 px-5 md:px-10 flex items-center justify-between border-b border-border">
                  <div className="flex items-center gap-2.5">
                    <Logo className="w-7 h-7" />
                    <span className="font-serif text-xl leading-none">TechsFlow</span>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="w-10 h-10 rounded-full border border-border flex items-center justify-center"
                    aria-label="Close menu"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="px-5 md:px-10 pt-10 pb-10 overflow-y-auto">
                  <div className="label-eyebrow mb-8">Menu</div>
                  <ul className="space-y-2">
                    {nav.map((n, i) => (
                      <motion.li
                        key={n.to}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.06 * i, duration: 0.4 }}
                      >
                        <NavLink
                          to={n.to}
                          className={({ isActive }) =>
                            `flex items-baseline justify-between border-b border-border py-4 ${
                              isActive ? 'text-foreground' : 'text-foreground/80'
                            }`
                          }
                        >
                          <span className="font-serif text-4xl tracking-tight">{n.label}</span>
                          <span className="font-mono text-xs text-muted-foreground">{n.n}</span>
                        </NavLink>
                      </motion.li>
                    ))}
                  </ul>
                  <div className="mt-10">
                    <Link to="/contact" className={buttonVariants({ variant: 'editorial', className: 'w-full justify-center' })}>
                      Start a project <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </FocusTrap>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
